import { Controller, useFormContext } from 'react-hook-form';

import { Button } from '@nesso/ui/primitives/button';
import { InputField } from '@nesso/ui/primitives/input-field';
import { MaskedInputField, US_PHONE_MASK } from '@nesso/ui/primitives/masked-input-field';

import {
    type AddClientContactInput,
    type EmailInUseError,
    MAX_INPUT_FIELD_LENGTH,
} from '@/entities/client';

import { formatContactEmailInUseMessage } from '../../../lib/format-contact-email-in-use-message';

type Props = {
    clientId: string;
    clientFirstName: string;
    emailInUse: EmailInUseError | null;
    onEmailChange: () => void;
    onUseExistingPerson: (personId: string) => void;
    isEmailLocked?: boolean;
    isPhoneLocked?: boolean;
};

export function AddClientContactFormDetails({
    clientId,
    clientFirstName,
    emailInUse,
    onEmailChange,
    onUseExistingPerson,
    isEmailLocked = false,
    isPhoneLocked = false,
}: Props) {
    const {
        control,
        formState: { isSubmitting },
    } = useFormContext<AddClientContactInput>();
    const emailInUseMessage = emailInUse
        ? formatContactEmailInUseMessage(emailInUse, clientId, clientFirstName)
        : null;

    return (
        <>
            <Controller
                control={control}
                name="emailPrimary"
                render={({ field, fieldState }) => (
                    <InputField
                        {...field}
                        value={field.value ?? ''}
                        id={field.name}
                        type="email"
                        label="Email (optional)"
                        disabled={isSubmitting || isEmailLocked}
                        maxLength={MAX_INPUT_FIELD_LENGTH}
                        onChange={(event) => {
                            field.onChange(event);
                            onEmailChange();
                        }}
                        error={
                            emailInUse && emailInUseMessage ? (
                                <>
                                    {emailInUseMessage.message}
                                    {emailInUseMessage.useExisting && (
                                        <>
                                            {' or '}
                                            <Button
                                                type="button"
                                                variant="inline-link"
                                                onClick={() =>
                                                    onUseExistingPerson(emailInUse.holder.personId)
                                                }
                                            >
                                                {emailInUseMessage.useExisting}
                                            </Button>
                                        </>
                                    )}
                                </>
                            ) : (
                                (fieldState.error?.message ?? fieldState.invalid)
                            )
                        }
                    />
                )}
            />
            <Controller
                control={control}
                name="phonePrimary"
                render={({ field, fieldState }) => (
                    <MaskedInputField
                        {...field}
                        value={field.value ?? ''}
                        id={field.name}
                        label="Phone number"
                        placeholder="(   )    -    "
                        mask={US_PHONE_MASK}
                        disabled={isSubmitting || isPhoneLocked}
                        error={fieldState.error?.message ?? fieldState.invalid}
                        className="max-w-37.75"
                    />
                )}
            />
        </>
    );
}
